import type { KeyboardEvent, MouseEvent, SyntheticEvent } from "react";
import type { FavoriteItemDTO } from "@packages/domain";
import { Badge, Button } from "@packages/ui";
import { VehicleCard } from "@webmotors/eleanor-ui/webmotors/vehicle-card";
import { formatOdometer, formatPrice } from "@/commons/number-format";
import { buildDetailUrl, buildImageUrls, buildSimulatorUrl } from "@/utils";
import { useIsMobile } from "@/hooks/use-is-mobile";
import styles from "./favorite-card.module.css";

type FavoriteCardProps = {
  favorite: FavoriteItemDTO;
  onRemove: () => void;
  isRemoving: boolean;
};

function extractStateAbbr(state: string | undefined): string {
  if (!state) return "";
  const start = state.indexOf("(");
  if (start < 0) return state;
  const end = state.indexOf(")", start + 1);
  return end > start ? state.substring(start + 1, end) : state;
}

function HeartIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.174 2.71078C9.928 2.51478 8.75 2.98678 8 3.90945C7.25 2.98678 6.07267 2.51345 4.826 2.71078C3.41933 2.93212 2 4.11412 2 6.25678C2 9.76345 6.226 12.6314 7.52133 13.4275C7.62667 13.4915 7.74733 13.5261 7.87067 13.5261H8.12933C8.25267 13.5261 8.37333 13.4915 8.47867 13.4275C9.774 12.6314 14 9.76345 14 6.25678C14 4.11412 12.5807 2.93212 11.174 2.71078Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.6687 6.66667C10.6687 7.402 10.0707 8 9.33533 8H2.66667C2.48933 8 2.32 8.07 2.19533 8.19533L1.33333 9.05733V2.66667C1.33333 1.93133 1.93133 1.33333 2.66667 1.33333H9.33533C10.0707 1.33333 10.6687 1.93133 10.6687 2.66667V6.66667ZM9.33533 0H2.66667C1.196 0 0 1.196 0 2.66667V10.6667C0 10.936 0.162 11.1793 0.411333 11.2827C0.494 11.3167 0.580667 11.3333 0.666667 11.3333C0.84 11.3333 1.01 11.2653 1.138 11.138L2.94267 9.33333H9.33533C10.806 9.33333 12.002 8.13733 12.002 6.66667V2.66667C12.002 1.196 10.806 0 9.33533 0ZM8.6664 2.66667H3.33307C2.96507 2.66667 2.6664 2.96533 2.6664 3.33333C2.6664 3.70133 2.96507 4 3.33307 4H8.6664C9.0344 4 9.33307 3.70133 9.33307 3.33333C9.33307 2.96533 9.0344 2.66667 8.6664 2.66667ZM7.33307 5.33333H3.33307C2.96507 5.33333 2.6664 5.632 2.6664 6C2.6664 6.368 2.96507 6.66667 3.33307 6.66667H7.33307C7.70107 6.66667 7.99973 6.368 7.99973 6C7.99973 5.632 7.70107 5.33333 7.33307 5.33333Z"
        fill="white"
      />
    </svg>
  );
}

function SantanderIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.58768 5.49254C8.57332 5.0597 8.45972 4.62687 8.24645 4.23881L6.36967 0.820896C6.22749 0.567164 6.12796 0.283582 6.07109 0L5.98578 0.149254C5.51659 1 5.51659 2.02985 5.98578 2.8806L7.49289 5.61194C7.96209 6.46269 7.96209 7.49254 7.49289 8.34328L7.40758 8.49254C7.35071 8.20896 7.25119 7.92537 7.10901 7.67164L5.72986 5.16418L4.86256 3.56716C4.72038 3.31343 4.62085 3.02985 4.56398 2.74627L4.47867 2.89552C4.00948 3.73134 4.00948 4.77612 4.47867 5.61194L6 8.34328C6.46919 9.19403 6.46919 10.2239 6 11.0746L5.91469 11.2239C5.85782 10.9403 5.75829 10.6567 5.61611 10.403L3.73934 6.98507C3.48341 6.52239 3.36967 6.01493 3.3981 5.50746C1.39337 6.0597 0 7.22388 0 8.58209C0 10.4627 2.6872 12 6 12C9.3128 12 12 10.4776 12 8.58209C11.9856 7.20896 10.6066 6.04478 8.58768 5.49254Z"
        fill="#E22446"
      />
    </svg>
  );
}

export function FavoriteCard({
  favorite,
  onRemove,
  isRemoving,
}: Readonly<FavoriteCardProps>) {
  const isMobile = useIsMobile();
  const isSold = favorite.active !== true;
  const isCar = favorite.vehicleType === 1;
  const detailUrl = buildDetailUrl(favorite);
  const simulatorUrl = buildSimulatorUrl();

  const openDetail = () => {
    window.open(detailUrl, "_blank", "noopener,noreferrer");
  };
  const handleCardKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openDetail();
    }
  };
  const stopPropagation = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  const cardStyle = isMobile ? "feed" : "compact";

  const title = [favorite.brand, favorite.model].filter(Boolean).join(" ");
  const details = [
    favorite.yearFabrication && favorite.yearModel
      ? `${favorite.yearFabrication}/${favorite.yearModel}`
      : "",
    favorite.odometer != null ? formatOdometer(favorite.odometer) : "",
  ]
    .filter(Boolean)
    .join(" • ");
  const stateAbbr = extractStateAbbr(favorite.state);
  const location = [favorite.city, stateAbbr].filter(Boolean).join(" - ");
  const price = favorite.price ? `R$ ${formatPrice(favorite.price)}` : "";
  const images = buildImageUrls(favorite.photos, isCar);

  if (isSold) {
    const badgeSize = isMobile ? "medium" : "small";
    const firstImage = images.length > 0 ? [images[0]] : images;

    return (
      <VehicleCard
        cardStyle={cardStyle}
        state="inactive"
        data-testid={`favorite-card-${favorite.advertiseId}`}
      >
        <VehicleCard.Image images={firstImage} cardStyle={cardStyle}>
          <VehicleCard.ActionButton
            onClick={onRemove}
            disabled={isRemoving}
            aria-label="Remover dos favoritos"
            data-testid={`remove-favorite-${favorite.advertiseId}`}
            data-action="favorite"
            style={{ pointerEvents: "auto" }}
            active
          >
            <HeartIcon />
          </VehicleCard.ActionButton>
          <VehicleCard.Badges>
            <Badge text="ANÚNCIO REMOVIDO" variant="neutral" size={badgeSize} />
          </VehicleCard.Badges>
        </VehicleCard.Image>
        <VehicleCard.Content>
          <VehicleCard.Info
            title={title}
            version={favorite.version}
            details={details}
            location={location}
            cardStyle={cardStyle}
          />
        </VehicleCard.Content>
      </VehicleCard>
    );
  }

  return (
    <VehicleCard
      cardStyle={cardStyle}
      state={isRemoving ? "inactive" : "default"}
      data-testid={`favorite-card-${favorite.advertiseId}`}
      role="link"
      tabIndex={0}
      aria-label={`Ver detalhes de ${title}`}
      onClick={openDetail}
      onKeyDown={handleCardKeyDown}
      style={{ cursor: "pointer" }}
    >
      <VehicleCard.Image images={images} cardStyle={cardStyle}>
        <VehicleCard.ActionButton
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            stopPropagation(e);
            onRemove();
          }}
          disabled={isRemoving}
          aria-label="Remover dos favoritos"
          data-testid={`remove-favorite-${favorite.advertiseId}`}
          data-action="favorite"
          active
        >
          <HeartIcon />
        </VehicleCard.ActionButton>
      </VehicleCard.Image>

      <VehicleCard.Content>
        <VehicleCard.Info
          title={title}
          version={favorite.version}
          details={details}
          location={location}
          cardStyle={cardStyle}
        />
        {price && <VehicleCard.Price value={price} cardStyle={cardStyle} />}
        <VehicleCard.Actions cardStyle={cardStyle}>
          {isMobile && (
            <Button
              tag="a"
              href={simulatorUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              size="small"
              className={styles.actionButton}
              onClick={stopPropagation}
            >
              <SantanderIcon />
              Simular
            </Button>
          )}
          <Button
            tag="a"
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="situational"
            size="small"
            className={styles.actionButton}
            onClick={stopPropagation}
          >
            <ChatIcon />
            Enviar mensagem
          </Button>
          {!isMobile && (
            <Button
              tag="a"
              href={simulatorUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              size="small"
              className={styles.actionButton}
              onClick={stopPropagation}
            >
              <SantanderIcon />
              Simular
            </Button>
          )}
        </VehicleCard.Actions>
      </VehicleCard.Content>
    </VehicleCard>
  );
}
